import React, { useEffect, useMemo, useState } from "react";
import { PageContainer } from "../components/Container/PageContainer";
import { getAllCollection } from "../utils/pureDataFetch";
import { 
  Box, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from "@mui/material";
import { dbService } from "../firebaseInstance";
import moment from "moment";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CategoryAssignModal from "../components/modal/CategoryAssignModal";
import CategoryManagerModal from "../components/modal/CategoryManagerModal";
import { 
  subscribeCategories,
  createCategory, 
  updateCategory,
  deleteCategory,
  assignCategoryToCollection 
} from "../services/adminCategoryManager";

const AdminCollectionManager = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [categories, setCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(true);
  const [catError, setCatError] = useState("");
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        setLoading(true);
        const allCollections = await getAllCollection();
        // Sort by createdAt in descending order (latest first)
        const sortedCollections = allCollections.sort((a, b) => {
          const aDate = a.createdAt ? (a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt)) : new Date(0);
          const bDate = b.createdAt ? (b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt)) : new Date(0);
          return bDate - aDate;
        });
        setCollections(sortedCollections);
      } catch (error) {
        console.error("Error fetching collections:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCollections();
  }, []);

  // Subscribe to collection documents for reactive updates
  useEffect(() => {
    // If we have many collections, and we know ids, we could attach listeners per doc.
    // For simplicity, re-fetch on category changes snapshot since assignments flow through categories too.
    // Additionally, poll or provide a manual refresh button if needed.
  }, [categories]);

  useEffect(() => {
    setCatLoading(true);
    const unsub = subscribeCategories(
      (list) => { setCategories(list); setCatLoading(false); },
      (e) => { console.error('Error fetching categories:', e); setCatError('카테고리를 불러오지 못했습니다.'); setCatLoading(false); }
    );
    return () => unsub && unsub();
  }, []);

  const handleMenuOpen = (event, collection) => {
    setAnchorEl(event.currentTarget);
    setSelectedCollection(collection);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCollection(null);
  };

  const handleView = () => {
    if (selectedCollection) {
      window.open(`/collection/${selectedCollection.id}`, "_blank");
    }
    handleMenuClose();
  };

  const openCategoryAssign = (collection) => {
    setSelectedCollection(collection);
    setCategoryModalOpen(true);
  };

  const handleAssignCategory = async (keysOrEmpty) => {
    if (!selectedCollection) return;
    const id = selectedCollection.id;
    // optimistic update
    const prev = selectedCollection;
    try {
      const keys = Array.isArray(keysOrEmpty) ? keysOrEmpty : [];
      const labels = keys
        .map((k) => {
          const cat = categories.find((c) => c.key === k);
          return (cat?.labels && (cat.labels.ko || cat.labels.en)) || null;
        })
        .filter(Boolean);
      setCollections((prevList) => prevList.map((c) => c.id === id ? { ...c, categoryKeys: keys, categoryLabels: labels } : c));
      await assignCategoryToCollection(id, keys);
    } catch (e) {
      console.error('Assign category failed', e);
      // rollback
      setCollections((prevList) => prevList.map((c) => c.id === id ? prev : c));
      alert(e.message || '카테고리 지정에 실패했습니다.');
      throw e;
    }
  };

  const handleCreateCategory = async (key, labels) => {
    await createCategory(key, labels);
  };

  const collectionsCountByCategory = useMemo(() => {
    const map = {};
    collections.forEach((c) => {
      const keys = c.categoryKeys && c.categoryKeys.length ? c.categoryKeys : [''];
      keys.forEach((k) => {
        map[k] = (map[k] || 0) + 1;
      });
    });
    return map;
  }, [collections]);

  const handleDelete = async () => {
    if (selectedCollection) {
      if (window.confirm(`정말로 "${selectedCollection.title || "이 컬렉션"}"을(를) 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`)) {
        try {
          await dbService.collection("collection").doc(selectedCollection.id).delete();
          setCollections(collections.filter(c => c.id !== selectedCollection.id));
          handleMenuClose();
        } catch (error) {
          console.error("Error deleting collection:", error);
          alert("컬렉션 삭제 중 오류가 발생했습니다.");
        }
      } else {
        handleMenuClose();
      }
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Collection Manager
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage all collections in the system
        </Typography>
      </Box>

      {/* Category Management Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>카테고리 관리</Typography>
        {catError ? (
          <Typography color="error" variant="body2">{catError}</Typography>
        ) : null}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          {catLoading ? (
            <CircularProgress size={20} />
          ) : (
            categories.filter((c) => c.isActive !== false).map((cat) => {
              const display = (cat.labels && (cat.labels.ko || cat.labels.en)) || cat.key;
              const count = collectionsCountByCategory[cat.key] || 0;
              return (
                <Chip key={cat.key} label={`${display} (${count})`} variant="outlined" title={`${cat.key} / ${cat.labels?.en || ''}`} />
              );
            })
          )}
          <Chip label={`Uncategorized (${collectionsCountByCategory[''] || 0})`} variant="outlined" />
        </Box>
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <IconButton size="small" onClick={() => setCategoryManagerOpen(true)}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Typography variant="caption" color="text.secondary">카테고리 전체 관리</Typography>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>User ID</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Categories</TableCell>
              <TableCell>Posts Count</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {collections.map((collection) => (
              <TableRow key={collection.id}>
                <TableCell>{collection.title || "Untitled"}</TableCell>
                <TableCell>{collection.userID || "Unknown"}</TableCell>
                <TableCell>
                  <Chip 
                    label={(collection.isPublic ?? true) ? "Public" : "Private"}
                    color={(collection.isPublic ?? true) ? "success" : "default"}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }} onClick={() => openCategoryAssign(collection)}>
                    {(collection.categoryLabels && collection.categoryLabels.length
                      ? collection.categoryLabels
                      : (collection.categoryKeys && collection.categoryKeys.length ? collection.categoryKeys : ['Uncategorized'])
                    ).map((label) => (
                      <Chip key={label} label={label} variant="outlined" size="small" sx={{ cursor: 'pointer' }} />
                    ))}
                  </Box>
                </TableCell>
                <TableCell>{collection.posts?.length || 0}</TableCell>
                <TableCell>
                  {collection.createdAt 
                    ? moment(collection.createdAt.toDate ? collection.createdAt.toDate() : collection.createdAt).format("YYYY-MM-DD HH:mm")
                    : "N/A"}
                </TableCell>
                <TableCell align="center">
                  <IconButton 
                    size="small"
                    onClick={(e) => handleMenuOpen(e, collection)}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {collections.length === 0 && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No collections found
          </Typography>
        </Box>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleView}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>컬렉션 보기</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>삭제</ListItemText>
        </MenuItem>
      </Menu>

      <CategoryAssignModal
        open={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        categories={categories}
        currentCategoryKeys={selectedCollection?.categoryKeys || []}
        onSubmit={handleAssignCategory}
      />

      <CategoryManagerModal
        open={categoryManagerOpen}
        onClose={() => setCategoryManagerOpen(false)}
        categories={categories}
        onCreate={async (key, labels) => { await handleCreateCategory(key, labels); }}
        onUpdate={async (key, updates) => { await updateCategory(key, updates); }}
        onDelete={async (key) => { await deleteCategory(key); }}
      />
    </PageContainer>
  );
};

export default AdminCollectionManager;